// ── FrameEngine: the radar + satellite animation, on ONE clock ──
//
// Why the old loop felt sluggish and choppy (measured, 2026-09-23):
//  • every opacity-slider move and every 10-min frame refresh tore down and
//    re-added ALL frame layers → every tile re-downloaded, the map blanked;
//  • no load gate: frames were shown before their tiles arrived, and GIBS's
//    newest instant is usually not tiled yet at display zooms (14/16 tiles
//    404) — the loop then DWELLED 1.5 s on that blank frame every cycle;
//  • hard cuts at a fixed 500 ms on 10-minute data.
//
// This engine:
//  • diffs frame sets by stable key — a refresh adds the new frame and drops
//    the oldest; nothing else reloads (explicit-timestamp URLs also keep the
//    browser cache warm);
//  • never shows a frame until its tiles are in, and treats a frame whose
//    tiles mostly failed as bad (skipped, never dwelt on);
//  • plays at a constant WEATHER speed (minutes per second), so 5-min and
//    10-min feeds move the storm at the same apparent pace;
//  • cross-fades: the incoming frame (stacked above) fades in while the
//    outgoing one fades out half a beat later — no opacity dip, and radar
//    echoes leave a short motion trail instead of popping;
//  • keeps the newest frame's tiles first in the request queue so the static
//    view appears before the loop finishes buffering.

import type maplibregl from "maplibre-gl";
import type { Family, Frame, FrameSet } from "./providers";

export interface EngineStatus {
  motion: boolean;
  playing: boolean;
  index: number; // position on the master timeline
  count: number; // master timeline length
  time: number | null; // valid time on screen (master family)
  ready: number; // frames whose tiles are in
  total: number;
  buffering: boolean;
  shownTime: Partial<Record<Family, number | null>>;
  future: { index: number; ready: number; total: number } | null; // futurecast frames
}

interface Slot {
  key: string;
  family: Family;
  frame: Frame;
  src: string;
  layer: string;
  op: number; // last opacity we set (skip redundant setPaintProperty calls)
}

// draw order, bottom → top
const FAMILIES: Family[] = ["ocean", "satbase", "sat", "meso", "radar", "surge", "lightning", "future", "futuredetail"];
// the futurecast and the impact zone's sharp picture over it: stepped together by the TimeDock
const FUTURE: Family[] = ["future", "futuredetail"];
// families that move with the clock (ocean fields and the surge map are static)
const ANIMATED: Family[] = ["satbase", "sat", "meso", "radar", "lightning"];
const BASE_MIN_PER_S = 25; // 1× = 25 minutes of weather per second (5-min frame every 200 ms)
const MIN_STEP_MS = 66;
const DWELL_MS = 1400; // hold on the newest frame before looping
const FADE_MAX = 240;
const BAD_TILE_SHARE = 0.3; // a frame with ≥30 % failed tiles is skipped
const PLAY_WHEN_READY = 0.5; // start looping once half the frames are in
const FUTURE_OPACITY = 0.95; // the futurecast is the subject; the live layers are its backdrop

type SourceCaches = Record<string, { _tiles?: Record<string, { state: string }> }>;

export class FrameEngine {
  private map: maplibregl.Map;
  private beforeId: () => string | undefined;
  private slots = new Map<string, Slot>();
  private sets: Partial<Record<Family, FrameSet | null>> = {};
  private opacity = 0.85;
  private motion = false;
  private playing = true;
  private speed = 1;
  private idx = -1;
  private shown: Partial<Record<Family, string | null>> = {};
  private raf = 0;
  private refreshRaf = 0;
  private nextAt = 0;
  private listeners = new Set<(s: EngineStatus) => void>();
  private lastEmit = 0;
  private emitTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  // futurecast: stepped by the TimeDock (setFutureIndex), not by the live clock
  private futureIdx = 0;
  private liveDim = 1;

  constructor(map: maplibregl.Map, beforeId: () => string | undefined) {
    this.map = map;
    this.beforeId = beforeId;
    map.on("error", this.onError);
    map.on("sourcedata", this.onSourceData);
  }

  // ── public API ──
  setFrameSets(sets: Partial<Record<Family, FrameSet | null>>) {
    this.sets = sets;
    this.reconcile();
  }
  setMotion(on: boolean) {
    if (on === this.motion) return;
    this.motion = on;
    this.playing = true;
    this.idx = -1;
    this.reconcile();
  }
  setOpacity(op: number) {
    this.opacity = op;
    this.reapplyOpacity();
  }
  /** Dim the live imagery (1 = normal) — it becomes the futurecast's backdrop. */
  setLiveDim(v: number) {
    if (v === this.liveDim) return;
    this.liveDim = v;
    this.reapplyOpacity();
  }
  /** Show futurecast step i (the nearest READY step at or before it while frames stream in). */
  setFutureIndex(i: number) {
    this.futureIdx = i;
    this.showFuture(true);
    this.emit(true);
  }
  private reapplyOpacity() {
    for (const fam of FAMILIES) {
      const k = this.shown[fam];
      if (k) this.fadeTo(k, this.famOpacity(fam), 0);
    }
    // under a futurecast the live imagery turns grey: now = ghost, future = colour
    for (const s of this.slots.values()) {
      if (this.map.getLayer(s.layer)) this.map.setPaintProperty(s.layer, "raster-saturation", this.famSaturation(s.family));
    }
  }
  private famOpacity(fam: Family) {
    return FUTURE.includes(fam) ? FUTURE_OPACITY : this.opacity * this.liveDim;
  }
  private famSaturation(fam: Family) {
    return !FUTURE.includes(fam) && this.liveDim < 1 ? -1 : 0;
  }
  setSpeed(s: number) {
    this.speed = s;
    this.emit(true);
  }
  togglePlay() {
    this.playing = !this.playing;
    if (this.playing) this.kick();
    this.emit(true);
  }
  /** jump to a master index (pauses — the user is scrubbing) */
  seek(i: number) {
    const m = this.masterFrames();
    if (!this.motion || !m.length) return;
    this.playing = false;
    this.showAt(Math.max(0, Math.min(m.length - 1, i)), false);
  }
  step(delta: 1 | -1) {
    const m = this.masterFrames();
    if (!this.motion || m.length < 2) return;
    this.playing = false;
    let i = this.idx < 0 ? m.length - 1 : this.idx;
    for (let k = 0; k < m.length; k++) {
      i = (i + delta + m.length) % m.length;
      const s = this.slots.get(m[i].key);
      if (s && this.isReady(s)) break;
    }
    this.showAt(i, false);
  }
  subscribe(fn: (s: EngineStatus) => void) {
    this.listeners.add(fn);
    fn(this.status());
    return () => { this.listeners.delete(fn); };
  }
  destroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    cancelAnimationFrame(this.refreshRaf);
    if (this.emitTimer) clearTimeout(this.emitTimer);
    this.map.off("error", this.onError);
    this.map.off("sourcedata", this.onSourceData);
    if (this.map.getStyle()) for (const s of this.slots.values()) this.removeSlot(s);
    this.slots.clear();
    this.listeners.clear();
  }

  // ── layer bookkeeping ──
  private wanted() {
    const out = new Map<string, { family: Family; frame: Frame }>();
    for (const fam of FAMILIES) {
      const set = this.sets[fam];
      if (!set) continue;
      // static view: the newest frame + one fallback (GIBS's newest instant
      // is often not tiled yet); motion: the whole loop; static families: newest
      const frames = FUTURE.includes(fam) ? set.frames
        : !ANIMATED.includes(fam) ? set.frames.slice(-1) : this.motion ? set.frames : set.frames.slice(-2);
      for (const f of frames) out.set(f.key, { family: fam, frame: f });
    }
    return out;
  }

  private reconcile() {
    if (this.destroyed || !this.map.getStyle()) return;
    const want = this.wanted();
    for (const [key, slot] of this.slots) {
      if (want.has(key)) continue;
      this.removeSlot(slot);
      this.slots.delete(key);
      if (this.shown[slot.family] === key) this.shown[slot.family] = null;
    }
    // newest first: its tiles enter MapLibre's request queue ahead of the loop's
    const add = [...want].filter(([k]) => !this.slots.has(k)).sort((a, b) => b[1].frame.time - a[1].frame.time);
    for (const [key, { family, frame }] of add) this.addSlot(key, family, frame);
    for (const fam of FAMILIES) if (!this.sets[fam]) this.shown[fam] = null;
    this.refresh();
  }

  private addSlot(key: string, family: Family, frame: Frame) {
    const layer = `ovl-${family}-${key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
    const src = `${layer}-src`;
    if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    if (this.map.getSource(src)) this.map.removeSource(src);
    if (frame.image) {
      this.map.addSource(src, { type: "image", url: frame.image.url, coordinates: frame.image.coordinates });
    } else {
      this.map.addSource(src, {
        type: "raster", tiles: frame.tiles ?? [], tileSize: frame.tileSize ?? 256, maxzoom: frame.maxzoom ?? 22,
        ...(frame.bounds ? { bounds: frame.bounds } : {}), attribution: frame.attribution,
      });
    }
    this.map.addLayer(
      {
        id: layer, type: "raster", source: src,
        paint: { "raster-opacity": 0, "raster-fade-duration": 0, "raster-saturation": this.famSaturation(family) },
      },
      this.layerAbove(family, frame.time),
    );
    this.slots.set(key, { key, family, frame, src, layer, op: 0 });
  }

  private removeSlot(s: Slot) {
    if (this.map.getLayer(s.layer)) this.map.removeLayer(s.layer);
    if (this.map.getSource(s.src)) this.map.removeSource(s.src);
  }

  /** z-order: chronological within a family, families in FAMILIES order,
   *  everything under the map labels */
  private layerAbove(family: Family, time: number): string | undefined {
    let above: Slot | null = null;
    for (const s of this.slots.values()) {
      if (s.family === family && s.frame.time > time && (!above || s.frame.time < above.frame.time)) above = s;
    }
    if (above) return above.layer;
    // else the bottom (oldest) layer of the nearest family stacked above this one
    for (const fam of FAMILIES.slice(FAMILIES.indexOf(family) + 1)) {
      let low: Slot | null = null;
      for (const s of this.slots.values()) if (s.family === fam && (!low || s.frame.time < low.frame.time)) low = s;
      if (low) return low.layer;
    }
    const b = this.beforeId();
    return b && this.map.getLayer(b) ? b : undefined;
  }

  // ── readiness ──
  private isReady(s: Slot): boolean {
    if (!this.map.getSource(s.src) || !this.map.isSourceLoaded(s.src)) return false;
    if (s.frame.image) return true;
    const style = this.map.style as unknown as { sourceCaches?: SourceCaches; tileManagers?: SourceCaches };
    const tiles = Object.values((style.sourceCaches ?? style.tileManagers)?.[s.src]?._tiles ?? {});
    if (!tiles.length) return true; // nothing requested (view outside the frame's bounds)
    const bad = tiles.filter((t) => t.state === "errored").length;
    return bad / tiles.length < BAD_TILE_SHARE;
  }

  private onSourceData = (e: { sourceId?: string }) => {
    if (!e.sourceId?.startsWith("ovl-") || this.refreshRaf) return;
    this.refreshRaf = requestAnimationFrame(() => { this.refreshRaf = 0; this.refresh(); });
  };

  // an error listener silences MapLibre's default console logging for ALL
  // errors — keep reporting everything that isn't an overlay tile failure
  private onError = (e: { sourceId?: string; error?: unknown }) => {
    if (e.sourceId?.startsWith("ovl-")) return; // handled: the frame is skipped
    console.error(e.error ?? e);
  };

  // ── display ──
  /** the animated family with the most frames drives the clock (the 1-min
   *  storm loop when it is on, else radar / satellite / lightning) */
  private masterSet(): FrameSet | null {
    let best: FrameSet | null = null;
    for (const fam of ANIMATED) {
      const set = this.sets[fam];
      if (set && (!best || set.frames.length > best.frames.length)) best = set;
    }
    return best;
  }
  private masterFrames(): Frame[] {
    return this.masterSet()?.frames ?? [];
  }
  private stepMs() {
    // constant weather speed, but never faster than ~15 fps: 1-minute
    // mesoscale frames at 25 min/s would otherwise flip at 25 fps
    return Math.max(MIN_STEP_MS, ((this.masterSet()?.cadenceMin ?? 5) / (BASE_MIN_PER_S * this.speed)) * 1000);
  }

  private newestReady(fam: Family): Slot | null {
    const set = this.sets[fam];
    if (!set) return null;
    const slots = set.frames.map((f) => this.slots.get(f.key)).filter((x): x is Slot => !!x);
    return slots.filter((x) => this.isReady(x)).pop() ?? slots[slots.length - 1] ?? null;
  }

  /** newest READY frame of a family at or before time T (half-cadence tolerance) */
  private pickFor(fam: Family, T: number): Slot | null {
    const set = this.sets[fam];
    if (!set) return null;
    const tol = set.cadenceMin * 30_000;
    let best: Slot | null = null;
    for (const f of set.frames) {
      if (f.time > T + tol) break;
      const s = this.slots.get(f.key);
      if (s && this.isReady(s)) best = s;
    }
    return best;
  }

  private refresh() {
    if (this.destroyed) return;
    if (this.motion && this.masterFrames().length >= 2) {
      if (this.idx < 0 || this.idx >= this.masterFrames().length) {
        this.idx = this.masterFrames().length - 1;
        this.showAt(this.idx, false);
      } else if (!this.playing) {
        this.showAt(this.idx, false); // paused: fill in as tiles arrive
      }
      this.kick();
    } else {
      // static: the newest good frame per family; while nothing is in yet,
      // the newest one anyway so tiles paint progressively
      for (const fam of FAMILIES) {
        if (FUTURE.includes(fam)) continue;
        const pick = this.newestReady(fam);
        if (pick) this.display(fam, pick.key, false);
      }
    }
    this.showFuture(false);
    this.emit();
  }

  private showAt(i: number, fade: boolean) {
    const m = this.masterFrames();
    if (!m[i]) return;
    this.idx = i;
    const T = m[i].time;
    for (const fam of FAMILIES) {
      if (FUTURE.includes(fam)) continue;
      const s = ANIMATED.includes(fam) ? this.pickFor(fam, T) : this.newestReady(fam);
      if (s) this.display(fam, s.key, fade);
    }
    this.emit(true);
  }

  private showFuture(fade: boolean) {
    const frames = this.sets.future?.frames;
    if (!frames?.length) return;
    const want = Math.max(0, Math.min(this.futureIdx, frames.length - 1));
    let at = -1;
    for (let i = want; i >= 0; i--) {
      const s = this.slots.get(frames[i].key);
      if (s && this.isReady(s)) {
        if (this.shown.future !== s.key) this.display("future", s.key, fade);
        at = i;
        break;
      }
    }
    // the impact zone's sharp picture of the SAME step, once it's in — never
    // an older step's over a newer whole-region one
    const det = this.sets.futuredetail?.frames;
    const ds = det && at >= 0 && det[at] ? this.slots.get(det[at].key) : undefined;
    if (ds && this.isReady(ds)) {
      if (this.shown.futuredetail !== ds.key) this.display("futuredetail", ds.key, fade);
    } else if (this.shown.futuredetail) {
      this.fadeTo(this.shown.futuredetail, 0, 0);
      this.shown.futuredetail = null;
    }
  }

  private display(fam: Family, key: string, fade: boolean) {
    const prev = this.shown[fam];
    const f = fade ? (FUTURE.includes(fam) ? 90 : Math.min(FADE_MAX, this.stepMs() * 0.45)) : 0;
    this.fadeTo(key, this.famOpacity(fam), f, 0);
    if (prev && prev !== key) this.fadeTo(prev, 0, f, f * 0.5);
    for (const s of this.slots.values()) {
      if (s.family === fam && s.key !== key && s.key !== prev && s.op !== 0) this.fadeTo(s.key, 0, 0);
    }
    this.shown[fam] = key;
  }

  private fadeTo(key: string, v: number, duration: number, delay = 0) {
    const s = this.slots.get(key);
    if (!s || !this.map.getLayer(s.layer)) return;
    if (s.op === v && duration === 0) return;
    this.map.setPaintProperty(s.layer, "raster-opacity-transition", { duration, delay });
    this.map.setPaintProperty(s.layer, "raster-opacity", v);
    s.op = v;
  }

  // ── the clock ──
  private kick() {
    if (!this.raf && this.motion && this.playing && !this.destroyed) this.raf = requestAnimationFrame(this.tick);
  }

  private tick = (now: number) => {
    this.raf = 0;
    if (this.destroyed || !this.motion || !this.playing) return;
    const m = this.masterFrames();
    if (m.length < 2) return;
    if (now >= this.nextAt) {
      const ready = m.map((f) => { const s = this.slots.get(f.key); return !!s && this.isReady(s); });
      const nReady = ready.filter(Boolean).length;
      if (nReady >= Math.max(2, Math.ceil(m.length * PLAY_WHEN_READY))) {
        let i = this.idx;
        for (let k = 0; k < m.length; k++) {
          i = (i + 1) % m.length;
          if (ready[i]) break;
        }
        this.showAt(i, true);
        const newestReady = ready.lastIndexOf(true);
        this.nextAt = now + (i === newestReady ? DWELL_MS : this.stepMs());
      } else {
        this.nextAt = now + 250; // buffering — look again shortly
        this.emit();
      }
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  // ── status ──
  private status(): EngineStatus {
    const m = this.masterFrames();
    let ready = 0, total = 0, fReady = 0, fTotal = 0;
    for (const s of this.slots.values()) {
      const ok = this.isReady(s);
      if (FUTURE.includes(s.family)) { fTotal++; if (ok) fReady++; continue; }
      total++;
      if (ok) ready++;
    }
    const shownTime: Partial<Record<Family, number | null>> = {};
    for (const fam of FAMILIES) {
      const k = this.shown[fam];
      shownTime[fam] = k ? this.slots.get(k)?.frame.time ?? null : null;
    }
    const idx = this.motion && m.length ? Math.max(0, Math.min(this.idx, m.length - 1)) : m.length - 1;
    return {
      motion: this.motion, playing: this.playing, index: idx, count: m.length,
      time: this.motion ? m[idx]?.time ?? null : (shownTime.radar ?? shownTime.sat ?? null),
      ready, total,
      buffering: this.motion && m.length >= 2 && ready < Math.max(2, Math.ceil(total * PLAY_WHEN_READY)),
      shownTime,
      future: fTotal ? { index: this.futureIdx, ready: fReady, total: fTotal } : null,
    };
  }

  private emit(force = false) {
    const now = performance.now();
    if (!force && now - this.lastEmit < 120) {
      // throttled — but never drop the LAST update (e.g. the final tile landing)
      if (!this.emitTimer) {
        this.emitTimer = setTimeout(() => { this.emitTimer = null; this.emit(true); }, 130);
      }
      return;
    }
    this.lastEmit = now;
    if (this.destroyed) return;
    const s = this.status();
    for (const l of this.listeners) l(s);
  }
}
